import type { ChatImage } from "@kwp/contracts";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { cx } from "../../ui";
import { useModalFocus } from "../../ui/overlay";
import { useChatImage, useObjectUrl } from "./images";

/**
 * A message's images (WP-058): thumbnails at their own proportions. Each opens full screen
 * (WP-059): the image as large as the screen allows on a dark backdrop, with Download and Close;
 * the arrow keys and the side buttons move between the message's images. Escape, Close or a click
 * outside the image closes it.
 */
export function MessageImages({ workspaceId, channelId, images }: { workspaceId: string; channelId: string; images: readonly ChatImage[] }) {
  const [open, setOpen] = useState<number | null>(null);
  if (images.length === 0) return null;
  return (
    <>
      <ul aria-label="Images" className="mt-1 flex flex-wrap gap-2">
        {images.map((image, i) => (
          <li key={image.id}>
            <Thumbnail workspaceId={workspaceId} channelId={channelId} image={image} label={`Image ${i + 1} of ${images.length}`} onOpen={() => setOpen(i)} />
          </li>
        ))}
      </ul>
      {open === null ? null : <ImageViewer workspaceId={workspaceId} channelId={channelId} images={images} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </>
  );
}

function Thumbnail({ workspaceId, channelId, image, label, onOpen }: { workspaceId: string; channelId: string; image: ChatImage; label: string; onOpen: () => void }) {
  const blob = useChatImage(workspaceId, channelId, image.id);
  const url = useObjectUrl(blob.data);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${label.toLowerCase()}`}
      className="block overflow-hidden rounded-lg border border-border bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
      style={{ aspectRatio: `${image.width} / ${image.height}` }}
    >
      {url === null ? (
        <span className="grid h-full max-h-60 w-60 max-w-full place-items-center text-xs text-muted">{blob.isError ? "Image not available" : "Loading image…"}</span>
      ) : (
        <img src={url} alt={label} width={image.width} height={image.height} className="block h-auto max-h-60 w-auto max-w-[min(15rem,70vw)] object-contain" />
      )}
    </button>
  );
}

const VIEWER_BUTTON = "grid size-11 shrink-0 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-white";

function ImageViewer({
  workspaceId,
  channelId,
  images,
  index,
  onIndex,
  onClose,
}: {
  workspaceId: string;
  channelId: string;
  images: readonly ChatImage[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  useModalFocus(panel, onClose, close);
  // The page behind stays still while the image fills the screen.
  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = before;
    };
  }, []);

  const image = images[index];
  const blob = useChatImage(workspaceId, channelId, image?.id ?? "");
  const url = useObjectUrl(blob.data);
  if (image === undefined) return null;
  const many = images.length > 1;
  const label = `Image ${index + 1} of ${images.length}`;
  const step = (by: number) => onIndex((index + by + images.length) % images.length);
  const onKeyDown = (e: KeyboardEvent) => {
    if (!many || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    e.preventDefault();
    step(e.key === "ArrowLeft" ? -1 : 1);
  };

  return (
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-50 flex flex-col bg-black/95 text-white outline-none"
    >
      <div className="flex items-center gap-2 px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2">
        <p className="min-w-0 flex-1 truncate text-sm text-white/80">{many ? label : "Image"}</p>
        {url === null ? null : (
          <a href={url} download={`image-${image.id}.${image.mimeType.split("/")[1] ?? "img"}`} className="rounded-md px-3 py-2 text-sm text-white hover:bg-white/10">
            Download
          </a>
        )}
        <button ref={close} type="button" onClick={onClose} className={VIEWER_BUTTON}>
          <span aria-hidden="true" className="text-xl leading-none">
            ×
          </span>
          <span className="sr-only">Close</span>
        </button>
      </div>
      {/* A click on the backdrop closes; a click on the image does not. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center p-2 sm:px-16" onClick={(e) => e.target === e.currentTarget && onClose()}>
        {url === null ? (
          <p className="text-sm text-white/70">{blob.isError ? "Image not available" : "Loading image…"}</p>
        ) : (
          <img src={url} alt={label} width={image.width} height={image.height} className="max-h-full max-w-full object-contain" />
        )}
        {many ? (
          <>
            <button type="button" onClick={() => step(-1)} aria-label="Previous image" className={cx(VIEWER_BUTTON, "absolute left-2 top-1/2 -translate-y-1/2")}>
              <span aria-hidden="true">‹</span>
            </button>
            <button type="button" onClick={() => step(1)} aria-label="Next image" className={cx(VIEWER_BUTTON, "absolute right-2 top-1/2 -translate-y-1/2")}>
              <span aria-hidden="true">›</span>
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
